import React, { useState, useEffect } from 'react';
import { FiTrendingUp, FiDollarSign, FiUsers } from 'react-icons/fi';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ownerAnalyticsAPI } from '../../../api/ownerEndpoints';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { showToast } from '../../../components/common/Toaster';
import { useTheme } from '../../../contexts/ThemeContext';
import { safeParseFloat, safeToFixed } from './utils';
import {
  Section,
  SectionTitle,
  Grid,
  StatCard,
  StatLabel,
  StatValue,
  StatDescription,
  Card,
  ChartContainer,
  LoadingContainer,
  EmptyState,
} from './Analytics.styles';

const Revenue = ({ startDate, endDate }) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = { 
        limit_months: 12,
        start_date: startDate ? `${startDate}T00:00:00Z` : undefined,
        end_date: endDate ? `${endDate}T23:59:59Z` : undefined,
      };
      const response = await ownerAnalyticsAPI.getRevenue(params);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching revenue:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'KES 0';
    return `KES ${parseFloat(amount).toLocaleString('en-KE')}`;
  };

  const prepareMRRTrend = () => {
    if (!data?.mrr_trend) return [];
    return data.mrr_trend.map((item) => ({
      month: item.month.split('-')[1],
      amount: safeParseFloat(item.amount),
    }));
  };

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner size="large" text="Loading revenue metrics..." />
      </LoadingContainer>
    );
  }

  if (!data) {
    return (
      <EmptyState>
        <FiDollarSign size={64} />
        <h3>No Revenue Data</h3>
        <p>Revenue metrics will appear when available</p>
      </EmptyState>
    );
  }

  const mrrData = prepareMRRTrend();

  return (
    <>
      <Section>
        <SectionTitle>
          <FiDollarSign />
          Revenue Metrics
        </SectionTitle>
        <Grid minWidth="250px">
          <StatCard>
            <StatLabel>Total Revenue</StatLabel>
            <StatValue>{formatCurrency(data.total_revenue)}</StatValue>
            <StatDescription>All-time platform revenue</StatDescription>
          </StatCard>

          <StatCard>
            <StatLabel>Revenue in Period</StatLabel>
            <StatValue>{formatCurrency(data.revenue_last_days)}</StatValue>
            <StatDescription>Recent revenue activity</StatDescription>
          </StatCard>

          <StatCard>
            <StatLabel>ARPU</StatLabel>
            <StatValue>KES {safeToFixed(data.arpu_last_days)}</StatValue>
            <StatDescription>Average Revenue Per User</StatDescription>
          </StatCard>

          <StatCard>
            <StatLabel>Paying Users</StatLabel>
            <StatValue>{safeParseFloat(data.paying_users).toLocaleString()}</StatValue>
            <StatDescription>Active paying customers</StatDescription>
          </StatCard>
        </Grid>
      </Section>

      {mrrData.length > 0 && (
        <Section>
          <SectionTitle>
            <FiTrendingUp />
            Monthly Recurring Revenue (MRR) Trend
          </SectionTitle>
          <Card>
            <ChartContainer height="300px">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={mrrData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
                  <XAxis dataKey="month" stroke={theme.colors.textSecondary} />
                  <YAxis stroke={theme.colors.textSecondary} />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: theme.colors.surface,
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: theme.borderRadius.md,
                    }}
                    formatter={(value) => formatCurrency(value)}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="amount" 
                    stroke={theme.colors.primary} 
                    strokeWidth={2}
                    dot={{ fill: theme.colors.primary }}
                    name="MRR"
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          </Card>
        </Section>
      )}
    </>
  );
};

export default Revenue;

